
import {
  ChangeDetectorRef,
  Component,
  OnInit
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import Swal from 'sweetalert2';

import { forkJoin } from 'rxjs';

import {
  CheckoutService,
  ReservaCalendario
} from '../../../service/checkout.service';

import {
  RegraMinimoNoites,
  RegrasMinimoNoitesService
} from '../../../service/RegraMinimoNoites.service';

import {
  ReservaService,
  CriarReservaRequest
} from '../../../service/reserva.service';

import {
  AdminReservaService,
  LinkPagamentoAdminResponse
} from '../../../service/admin-reserva.service';

import {
  Quarto,
  QuartoService
} from '../../../service/quarto.service';

import {
  PacoteAdicional,
  PacoteAdicionalService
} from '../../../service/pacote-adicional.service';

export interface HospedeAdminRequest {
  nome: string;
  sobrenome: string;
  email: string;
  telefone: string;
  cpf: string;
}

interface DiaCalendario {
  numero: number;
  data: Date;
  hoje: boolean;
  selecionado: boolean;
  intervalo: boolean;
  desabilitado: boolean;
}

@Component({
  selector: 'app-nova-reserva',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './nova-reserva.component.html',
  styleUrl: './nova-reserva.component.css'
})
export class NovaReservaComponent implements OnInit {

  reservasOcupadas: ReservaCalendario[] = [];
  regrasMinimoNoites: RegraMinimoNoites[] = [];

  mostrarCalendario = false;
  modoCalendario: 'entrada' | 'saida' = 'entrada';

  carregandoCalendario = false;
  erroCalendario = false;

  mesAtual = new Date().getMonth();
  anoAtual = new Date().getFullYear();

  diasCalendario: DiaCalendario[] = [];

  readonly nomesMeses = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril',
    'Maio', 'Junho', 'Julho', 'Agosto',
    'Setembro', 'Outubro', 'Novembro', 'Dezembro'
  ];

  quartos: Quarto[] = [];
  pacotes: PacoteAdicional[] = [];
  hospedes: HospedeAdminRequest[] = [];
  quartoId: number | null = null;
  checkIn = '';
  checkOut = '';
  adultos = 1;
  criancas = 0;
  animais = 0;
  codigoCupom = '';

  pacotesSelecionados: number[] = [];

  carregando = false;
  salvando = false;

  reservaCriadaId: number | null = null;
  linkPagamento: LinkPagamentoAdminResponse | null = null;

  constructor(
    private readonly adminReservaService: AdminReservaService,
    private readonly quartoService: QuartoService,
    private readonly pacoteService: PacoteAdicionalService,
    private readonly cdr: ChangeDetectorRef,
    private readonly reservaService: ReservaService,
    private readonly checkoutService: CheckoutService,
    private readonly regrasMinimoNoitesService: RegrasMinimoNoitesService
  ) {}

  ngOnInit(): void {
    this.sincronizarHospedes();
    this.carregarQuartos();
  }

  get quantidadeHospedes(): number {
    return this.adultos + this.criancas;
  }

  get quartoSelecionado(): Quarto | undefined {
    return this.quartos.find(q => q.id === Number(this.quartoId));
  }

  get pacotesDisponiveis(): PacoteAdicional[] {
    return this.pacotes.filter(p =>
      p.ativo && p.quartoId === Number(this.quartoId)
    );
  }

  get quantidadeNoites(): number {
    if (!this.checkIn || !this.checkOut) return 0;

    const entrada = Date.parse(`${this.checkIn}T12:00:00`);
    const saida = Date.parse(`${this.checkOut}T12:00:00`);

    return Math.max(
      0,
      Math.round((saida - entrada) / 86400000)
    );
  }

  carregarQuartos(): void {
    this.carregando = true;

    this.quartoService.listar().subscribe({
      next: quartos => {
        this.quartos = quartos;
        this.carregando = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.carregando = false;
        this.cdr.detectChanges();
        this.alerta('error', 'Erro', 'Não foi possível carregar as acomodações.');
      }
    });
  }


  private converterData(data: string): Date {
    const [ano, mes, dia] = data.split('-').map(Number);
    return new Date(ano, mes - 1, dia);
  }

  private formatarDataBackend(data: Date): string {
    const ano = data.getFullYear();
    const mes = String(data.getMonth() + 1).padStart(2, '0');
    const dia = String(data.getDate()).padStart(2, '0');

    return `${ano}-${mes}-${dia}`;
  }

  formatarData(data: string | null): string {
    if (!data) return 'Selecione a data';

    return this.converterData(data)
      .toLocaleDateString('pt-BR');
  }

  get dataMinimaDate(): Date {
    const hoje = new Date(
      new Date().getFullYear(),
      new Date().getMonth(),
      new Date().getDate()
    );

    hoje.setDate(hoje.getDate() + 1);
    return hoje;
  }

  private irParaMesMinimo(): void {
    this.mesAtual = this.dataMinimaDate.getMonth();
    this.anoAtual = this.dataMinimaDate.getFullYear();
  }

  get minimoNoitesSelecionado(): number {
    if (!this.checkIn) return 1;

    const regra = this.regrasMinimoNoites.find(r =>
      r.data === this.checkIn && r.ativo
    );

    return Math.max(1, Number(regra?.minimoNoites ?? 1));
  }

  get checkoutMinimo(): string | null {
    if (!this.checkIn) return null;

    const data = this.converterData(this.checkIn);
    data.setDate(
      data.getDate() + this.minimoNoitesSelecionado
    );

    return this.formatarDataBackend(data);
  }

  private minimoParaData(data: string): number {
    const regra = this.regrasMinimoNoites.find(r =>
      r.data === data && r.ativo
    );

    return Math.max(1, Number(regra?.minimoNoites ?? 1));
  }

  private possuiConflito(
    entrada: string,
    saida: string
  ): boolean {
    return this.reservasOcupadas.some(reserva =>
      entrada < reserva.checkOut &&
      saida > reserva.checkIn
    );
  }

  private dataOcupada(data: string): boolean {
    return this.reservasOcupadas.some(reserva =>
      data >= reserva.checkIn &&
      data < reserva.checkOut
    );
  }

  abrirCalendario(modo: 'entrada' | 'saida'): void {
    if (
      this.quartoId === null ||
      this.salvando ||
      this.reservaCriadaId !== null ||
      this.carregandoCalendario ||
      this.erroCalendario
    ) return;

    this.modoCalendario =
      modo === 'saida' && !this.checkIn
        ? 'entrada'
        : modo;

    const referencia =
      this.modoCalendario === 'saida'
        ? this.checkoutMinimo
        : this.checkIn;

    const data = referencia
      ? this.converterData(referencia)
      : this.dataMinimaDate;

    this.mesAtual = data.getMonth();
    this.anoAtual = data.getFullYear();

    this.mostrarCalendario = true;
    this.gerarCalendario();
  }

  gerarCalendario(): void {
    const primeiroDia = new Date(
      this.anoAtual, this.mesAtual, 1
    );

    const ultimoDia = new Date(
      this.anoAtual, this.mesAtual + 1, 0
    );

    const dias: DiaCalendario[] = [];

    for (let i = 0; i < primeiroDia.getDay(); i++) {
      dias.push({
        numero: 0,
        data: new Date(0),
        hoje: false,
        selecionado: false,
        intervalo: false,
        desabilitado: true
      });
    }

    const hoje = this.formatarDataBackend(new Date());
    const minima = this.formatarDataBackend(
      this.dataMinimaDate
    );

    for (let numero = 1; numero <= ultimoDia.getDate(); numero++) {
      const data = new Date(
        this.anoAtual, this.mesAtual, numero
      );

      const dataString = this.formatarDataBackend(data);

      let desabilitado =
        dataString < minima ||
        this.carregandoCalendario ||
        this.erroCalendario;

      if (this.modoCalendario === 'entrada') {
        const minimo = this.minimoParaData(dataString);
        const saidaMinima = new Date(data);

        saidaMinima.setDate(
          saidaMinima.getDate() + minimo
        );

        desabilitado ||= this.possuiConflito(
          dataString,
          this.formatarDataBackend(saidaMinima)
        );
      } else {
        desabilitado ||=
          !this.checkIn ||
          dataString < (this.checkoutMinimo ?? '') ||
          this.possuiConflito(
            this.checkIn,
            dataString
          );
      }

      dias.push({
        numero,
        data,
        hoje: dataString === hoje,
        selecionado:
          dataString === this.checkIn ||
          dataString === this.checkOut,
        intervalo:
          !!this.checkIn &&
          !!this.checkOut &&
          dataString > this.checkIn &&
          dataString < this.checkOut,
        desabilitado
      });
    }

    this.diasCalendario = dias;
  }

  selecionarData(dia: DiaCalendario): void {
    if (dia.numero === 0 || dia.desabilitado) return;

    const data = this.formatarDataBackend(dia.data);

    if (this.modoCalendario === 'entrada') {
      this.checkIn = data;
      this.checkOut = '';
      this.modoCalendario = 'saida';

      const minima = this.converterData(
        this.checkoutMinimo!
      );

      this.mesAtual = minima.getMonth();
      this.anoAtual = minima.getFullYear();
    } else {
      if (!this.checkIn || data < this.checkoutMinimo!) {
        return;
      }

      if (this.possuiConflito(this.checkIn, data)) {
        return;
      }

      this.checkOut = data;
      this.mostrarCalendario = false;
    }

    this.gerarCalendario();
    this.cdr.detectChanges();
  }

  mesAnterior(): void {
    const minimo = this.dataMinimaDate;

    if (
      this.anoAtual < minimo.getFullYear() ||
      (
        this.anoAtual === minimo.getFullYear() &&
        this.mesAtual <= minimo.getMonth()
      )
    ) return;

    const data = new Date(
      this.anoAtual, this.mesAtual - 1, 1
    );

    this.mesAtual = data.getMonth();
    this.anoAtual = data.getFullYear();

    this.gerarCalendario();
  }

  mesProximo(): void {
    const data = new Date(
      this.anoAtual, this.mesAtual + 1, 1
    );

    this.mesAtual = data.getMonth();
    this.anoAtual = data.getFullYear();

    this.gerarCalendario();
  }

  formatarDiaCalendario(data: Date): string {
    return this.formatarDataBackend(data);
  }

  selecionarQuarto(): void {
    this.checkIn = '';
    this.checkOut = '';

    this.pacotesSelecionados = [];
    this.reservasOcupadas = [];
    this.regrasMinimoNoites = [];

    this.mostrarCalendario = false;
    this.erroCalendario = false;

    this.adultos = 1;
    this.criancas = 0;
    this.animais = 0;

    this.sincronizarHospedes();

    if (this.quartoId === null) {
      this.diasCalendario = [];
      return;
    }

    this.carregandoCalendario = true;

    forkJoin({
      reservas: this.checkoutService.buscarDatasOcupadas(
        Number(this.quartoId)
      ),
      regras: this.regrasMinimoNoitesService.listarPublicas(
        Number(this.quartoId)
      ),
      pacotes: this.pacoteService.listarTodos()
    }).subscribe({
      next: resultado => {
        this.reservasOcupadas = resultado.reservas ?? [];
        this.regrasMinimoNoites = resultado.regras ?? [];
        this.pacotes = resultado.pacotes ?? [];

        this.carregandoCalendario = false;
        this.erroCalendario = false;

        this.irParaMesMinimo();
        this.gerarCalendario();
        this.cdr.detectChanges();
      },
      error: () => {
        this.carregandoCalendario = false;
        this.erroCalendario = true;
        this.diasCalendario = [];
        this.cdr.detectChanges();

        this.alerta(
          'error',
          'Erro ao carregar calendário',
          'Não foi possível consultar a disponibilidade e as regras desta acomodação.'
        );
      }
    });
  }

  sincronizarHospedes(): void {
    const total = Math.max(1, this.quantidadeHospedes);

    while (this.hospedes.length < total) {
      this.hospedes.push({
        nome: '',
        sobrenome: '',
        email: '',
        telefone: '',
        cpf: ''
      });
    }

    if (this.hospedes.length > total) {
      this.hospedes = this.hospedes.slice(0, total);
    }
  }

  alterarOcupacao(): void {
    this.adultos = Math.max(
      1, Math.floor(Number(this.adultos) || 1)
    );

    this.criancas = Math.max(
      0, Math.floor(Number(this.criancas) || 0)
    );

    this.animais = Math.max(
      0, Math.min(2, Math.floor(Number(this.animais) || 0))
    );

    const capacidade = this.quartoSelecionado?.capacidade;

    if (capacidade !== undefined) {
      if (this.adultos > capacidade) {
        this.adultos = capacidade;
      }

      if (this.adultos + this.criancas > capacidade) {
        this.criancas = capacidade - this.adultos;

        this.alerta(
          'warning',
          'Capacidade máxima atingida',
          `Esta acomodação permite no máximo ${capacidade} hóspedes, incluindo adultos e crianças.`
        );
      }
    }

    this.sincronizarHospedes();
    this.cdr.detectChanges();
  }

  alternarPacote(id: number): void {
    if (this.pacotesSelecionados.includes(id)) {
      this.pacotesSelecionados =
        this.pacotesSelecionados.filter(p => p !== id);
    } else {
      this.pacotesSelecionados = [
        ...this.pacotesSelecionados,
        id
      ];
    }
  }

  somenteNumeros(valor: string, limite: number): string {
    return (valor || '').replace(/\D/g, '').slice(0, limite);
  }

  get dataMinima(): string {
    const hoje = new Date();
    const ano = hoje.getFullYear();
    const mes = String(hoje.getMonth() + 1).padStart(2, '0');
    const dia = String(hoje.getDate()).padStart(2, '0');

    return `${ano}-${mes}-${dia}`;
  }

  formatarCpf(valor: string): string {
    return this.somenteNumeros(valor, 11)
      .replace(/^(\d{3})(\d)/, '$1.$2')
      .replace(/^(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
      .replace(/\.(\d{3})(\d)/, '.$1-$2');
  }

  formatarTelefone(valor: string): string {
    const telefone = this.somenteNumeros(valor, 11);

    if (telefone.length <= 2) {
      return telefone ? `(${telefone}` : '';
    }

    const ddd = telefone.slice(0, 2);
    const numero = telefone.slice(2);

    if (numero.length <= 4) {
      return `(${ddd}) ${numero}`;
    }

    if (numero.length <= 8) {
      return `(${ddd}) ${numero.slice(0, 4)}-${numero.slice(4)}`;
    }

    return `(${ddd}) ${numero.slice(0, 5)}-${numero.slice(5)}`;
  }

  private validar(): string | null {
    const quarto = this.quartoSelecionado;

    if (!quarto) return 'Selecione uma acomodação.';

    if (quarto.status !== 'DISPONIVEL') {
      return 'A acomodação está indisponível.';
    }

    if (this.carregandoCalendario || this.erroCalendario) {
      return 'Não foi possível confirmar a disponibilidade.';
    }

    if (this.checkIn < this.formatarDataBackend(this.dataMinimaDate)) {
      return 'O check-in deve ser a partir de amanhã.';
    }

    if (!this.checkoutMinimo || this.checkOut < this.checkoutMinimo) {
      return `Esta data exige no mínimo ${this.minimoNoitesSelecionado} noites.`;
    }

    if (this.possuiConflito(this.checkIn, this.checkOut)) {
      return 'O período selecionado possui datas ocupadas.';
    }

    if (!this.checkIn || !this.checkOut || this.quantidadeNoites < 1) {
      return 'Informe um período válido de hospedagem.';
    }

    if (this.adultos < 1) return 'Informe pelo menos um adulto.';

    if (this.quantidadeHospedes > quarto.capacidade) {
      return `A capacidade máxima é de ${quarto.capacidade} hóspedes.`;
    }

    if (this.animais < 0 || this.animais > 2) {
      return 'São permitidos no máximo 2 animais.';
    }

    for (let i = 0; i < this.hospedes.length; i++) {
      const h = this.hospedes[i];

      if (!h.nome.trim() || !h.sobrenome.trim()) {
        return `Informe o nome e sobrenome do hóspede ${i + 1}.`;
      }

      const telefone = this.somenteNumeros(h.telefone, 13);

      if (telefone.length < 10 || telefone.length > 13) {
        return `Informe um telefone válido para o hóspede ${i + 1}.`;
      }

      if (i === 0) {
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(h.email.trim())) {
          return 'Informe o e-mail válido do responsável.';
        }

        if (this.somenteNumeros(h.cpf, 11).length !== 11) {
          return 'Informe o CPF do responsável.';
        }
      }
    }

    return null;
  }


  criarReserva(): void {

    if (this.salvando || this.reservaCriadaId !== null) {
      return;
    }

    const erro = this.validar();

    if (erro) {
      this.alerta(
        'warning',
        'Verifique os dados',
        erro
      );
      return;
    }

    const dados: CriarReservaRequest = {
      quartoId: Number(this.quartoId),
      checkIn: this.checkIn,
      checkOut: this.checkOut,

      quantidadeHospedes: this.quantidadeHospedes,
      quantidadeCriancas: this.criancas,
      quantidadeAnimais: this.animais,

      hospedes: this.hospedes.map(h => ({
        nome: h.nome.trim(),
        sobrenome: h.sobrenome.trim(),
        email: h.email.trim(),
        telefone: this.somenteNumeros(h.telefone, 13),
        cpf: this.somenteNumeros(h.cpf, 11)
      }))
    };

    Swal.fire({
      icon: 'question',
      title: 'Criar reserva administrativa?',
      text: 'Deseja criar a reserva e gerar o link de pagamento Asaas?',
      showCancelButton: true,
      confirmButtonText: 'Criar e gerar link',
      cancelButtonText: 'Voltar',
      confirmButtonColor: '#173b63'
    }).then(resultado => {

      if (!resultado.isConfirmed || this.salvando) {
        return;
      }

      this.salvando = true;
      this.cdr.detectChanges();

      this.checkoutService.verificarDisponibilidade(
        Number(this.quartoId),
        this.checkIn,
        this.checkOut
      )

      this.reservaService.criar(dados).subscribe({

        next: reserva => {

          this.reservaCriadaId = reserva.id;
          this.cdr.detectChanges();

          this.gerarLink(reserva.id);
        },

        error: erro => {

          this.salvando = false;
          this.cdr.detectChanges();

          this.alerta(
            'error',
            'Erro ao criar reserva',
            this.mensagemErro(erro)
          );
        }
      });
    });
  }


  gerarLink(reservaId: number): void {
    this.salvando = true;

    this.adminReservaService.gerarLinkPagamento(reservaId).subscribe({
      next: link => {
        this.linkPagamento = link;
        this.salvando = false;
        this.cdr.detectChanges();

        this.alerta(
          'success',
          'Link gerado!',
          `Reserva #${reservaId} criada. Copie o link e envie ao cliente.`
        );
      },
      error: e => {
        this.salvando = false;
        this.cdr.detectChanges();

        this.alerta(
          'error',
          'Reserva criada, mas o link não foi gerado',
          `Reserva #${reservaId}. ${this.mensagemErro(e)} Você pode tentar gerar o link novamente.`
        );
      }
    });
  }

  async copiarLink(): Promise<void> {
    const url = this.linkPagamento?.invoiceUrl;
    if (!url) return;

    try {
      await navigator.clipboard.writeText(url);
      this.alerta('success', 'Copiado!', 'Link copiado para a área de transferência.');
    } catch {
      this.alerta('warning', 'Não foi possível copiar', 'Selecione e copie o endereço manualmente.');
    }
  }

  abrirLink(): void {
    const url = this.linkPagamento?.invoiceUrl;

    if (url && url.startsWith('https://')) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  }

  private mensagemErro(erro: any): string {
    return erro?.error?.message
      || erro?.error?.mensagem
      || (typeof erro?.error === 'string' ? erro.error : null)
      || 'Verifique os dados e tente novamente.';
  }

  private alerta(
    icon: 'success' | 'error' | 'warning',
    title: string,
    text: string
  ): void {
    Swal.fire({
      icon,
      title,
      text,
      confirmButtonColor: '#173b63'
    });
  }
}
